// ========================================
// pages/ResetPassword.js - Set New Password
// ========================================
// PROJECT: Agyapadie Vault - Digital Inheritance Platform
// LAST MODIFIED: April 12, 2026
// 
// PURPOSE: Allow users to set a new password using a reset token
// 
// FLOW:
// 1. User clicks email link with token
// 2. Page validates token
// 3. User enters new password
// 4. Password is updated
// 
// SECURITY:
// - Token must be valid and not expired
// - Password must be at least 6 characters
// - Token is single-use
// ========================================

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [checkingToken, setCheckingToken] = useState(true);

  // ========================================
  // VALIDATE TOKEN ON PAGE LOAD
  // ========================================
  useEffect(() => {
    const validateToken = async () => {
      try {
        await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/reset-password/${token}`);
        setTokenValid(true);
      } catch (err) {
        setTokenValid(false);
        setError(err.response?.data?.error || 'Invalid or expired reset link');
      } finally {
        setCheckingToken(false);
      }
    };
    
    if (token) {
      validateToken();
    } else {
      setTokenValid(false);
      setError('No reset token provided');
      setCheckingToken(false);
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/reset-password/${token}`, { password });
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (checkingToken) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-card">
          <div className="logo-container">
            <img src="/images/symbols/logo-light.png" alt="Agyapadie Vault" className="logo" />
          </div>
          <div className="loading-spinner"></div>
          <p>Verifying reset link...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-card">
          <div className="logo-container">
            <img src="/images/symbols/logo-light.png" alt="Agyapadie Vault" className="logo" />
          </div>
          <div className="error-icon">⚠️</div>
          <h2 className="title">Invalid Reset Link</h2>
          <p className="subtitle">{error}</p>
          <Link to="/forgot-password" className="resend-link">Request a new reset link →</Link>
          <div className="footer-links">
            <Link to="/login" className="footer-link">Back to Login</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <div className="reset-password-card">
        <div className="logo-container">
          <img src="/images/symbols/logo-light.png" alt="Agyapadie Vault" className="logo" />
        </div>
        
        {!success ? (
          <>
            <h1 className="title">Create New Password</h1>
            <p className="subtitle">Enter your new password below.</p>
            
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handleSubmit} className="form">
              <div className="form-group">
                <label className="label">New Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input"
                  placeholder="Minimum 6 characters"
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="label">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input"
                  placeholder="Re-enter your password"
                  required
                />
              </div>
              
              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Saving...' : 'Reset Password'}
              </button>
            </form>
            
            <div className="footer-links">
              <Link to="/login" className="footer-link">Back to Login</Link>
            </div>
          </>
        ) : (
          <>
            <div className="success-icon">✅</div>
            <h2 className="title">Password Reset Successful!</h2>
            <p className="subtitle">
              Your password has been changed. You will be redirected to login shortly.
            </p>
            <Link to="/login" className="login-link">Go to Login →</Link>
          </>
        )}
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .reset-password-page {
          min-height: 100vh;
          background: #1A1A1A;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }
        
        .reset-password-card {
          background: white;
          border-radius: 12px;
          padding: 40px;
          max-width: 450px;
          width: 100%;
          box-shadow: 0 10px 30px rgba(0,0,0,0.2);
          text-align: center;
        }
        
        .logo-container {
          text-align: center;
          margin-bottom: 30px;
        }
        
        .logo {
          height: 50px;
          width: auto;
        }
        
        .title {
          font-size: 24px;
          font-weight: bold;
          color: #1A1A1A;
          margin-bottom: 15px;
        }
        
        .subtitle {
          font-size: 14px;
          color: #666;
          margin-bottom: 25px;
        }
        
        .form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
          text-align: left;
        }
        
        .label {
          font-size: 14px;
          font-weight: bold;
          color: #1A1A1A;
        }
        
        .input {
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
          width: 100%;
          box-sizing: border-box;
        }
        
        .input:focus {
          outline: none;
          border-color: #FFD700;
        }
        
        .submit-btn {
          padding: 14px;
          background: #FFD700;
          color: #1A1A1A;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s;
          margin-top: 10px;
        }
        
        .submit-btn:hover {
          background: #B8860B;
        }
        
        .error-message {
          background: #fee2e2;
          border: 1px solid #ef4444;
          color: #dc2626;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        
        .error-icon {
          font-size: 48px;
          margin-bottom: 15px;
        }
        
        .success-icon {
          font-size: 48px;
          margin-bottom: 15px;
        }
        
        .resend-link {
          display: inline-block;
          color: #FFD700;
          text-decoration: none;
          margin-top: 15px;
        }
        
        .login-link {
          display: inline-block;
          color: #FFD700;
          text-decoration: none;
          font-weight: bold;
          margin-top: 20px;
        }
        
        .footer-links {
          margin-top: 25px;
          padding-top: 20px;
          border-top: 1px solid #eee;
        }
        
        .footer-link {
          color: #2E7D32;
          text-decoration: none;
          font-size: 14px;
        }
        
        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #F5F0E6;
          border-top: 4px solid #FFD700;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 480px) {
          .reset-password-card {
            padding: 25px;
          }
          
          .title {
            font-size: 20px;
          }
        }
      `}} />
    </div>
  );
};

export default ResetPassword;